/**
 * ROCKY-VIEW-EAST — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 6 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_ROCKY_VIEW_EAST: Location[] = [
// ──────────────────────────────────────────────────────────────── BALZAC ──
  {
    slug: "balzac",
    name: "Balzac",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.2, lng: -114 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Balzac sits about 9 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From the air, Balzac's calendar lives in its fields. CrossIron Mills reads the same in midwinter as in midsummer, while the cropland around the Rocky View County Balzac Commercial Corridor runs its whole cycle — bare soil, green-up, swathed rows, stubble under snow.",
      consequence:
        "Whatever the fields are doing on shoot day becomes the frame. A leasing or marketing shoot planned from a calendar can put a commercial site against bare black soil when the client imagined green, and from the road that backdrop is invisible until the aircraft is up.",
      resolution:
        "The date is set against the state of the ground, not the month. Fly4MEdia holds the booking for the window when the surrounding land carries the colour the material needs, files the CYYC authorisation as part of booking, and flies the approach run in the standard turnaround.",
    },
    landmarks: [
      "CrossIron Mills",
      "Balzac Farmers' Market",
      "Rocky View County Balzac Commercial Corridor",
      "Balzac Road",
      "Range Road 292",
    ],
    terrain:
      "Flat cropland at Calgary's north edge, interrupted by the roofs and parking fields of the commercial corridor; from altitude the retail development reads as an island set into working quarter-sections.",
    windNote:
      "Chinook belt, wide open. Nothing between the foothills and these fields slows a west wind, and the corridor's large flat roofs add mechanical turbulence close to the buildings, so approach lines are planned upwind.",
    seasonalWindow:
      "Green-up and harvest carry the frame — the corridor's buildings are constant while the surrounding fields supply the colour and the pattern. Snow inverts the picture, turning the dark roofs into the graphic element against white ground.",
    bylawNote:
      "Rocky View County's rules govern takeoff and landing on municipal land, and the corridor's commercial parcels are private property — launches are staged from the client's own site with permission.",
    subjectMix: ["commercial-retail", "industrial-yard", "agricultural"],
    mobilisationNote:
      "About 25 minutes from the Calgary base, close enough for same-day work once the CYYC authorisation is in hand. A Balzac visit pairs naturally with Dalroy, Delacour or Kathyrn on a single trip.",
    faqs: [
      {
        question: "Is Balzac inside Calgary's controlled airspace?",
        answer:
          "Yes. Balzac sits about 9 km from Calgary International, inside the CYYC Class C control zone as charted. Flights are conducted under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot date is confirmed, and the specific site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "Can you photograph commercial property around CrossIron Mills?",
        answer:
          "Yes. The commercial corridor is the main reason clients book Balzac. Because the area sits inside the CYYC control zone, the authorisation is filed as part of booking rather than added afterwards, and the flight plan is built around the approved altitude for the specific site.",
      },
      {
        question: "When is the best season for an aerial shoot in Balzac?",
        answer:
          "It depends on the backdrop the material needs. The corridor's buildings look the same year-round from altitude; the surrounding cropland does not. Green-up gives the strongest colour, harvest gives pattern and texture, and snow simplifies everything. The booking is timed to the state of the fields, not the month.",
      },
    ],
    primaryKeywords: [
      "drone photography Balzac",
      "aerial photography Balzac",
      "Balzac drone services",
    ],
    nearestLocations: ["dalroy", "keoma", "kathyrn", "delacour"],
  },

  // ────────────────────────────────────────────────────────────── DALEMEAD ──
  {
    slug: "dalemead",
    name: "Dalemead",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 50.9333, lng: -113.6167 },
    tier: 4,
    driveMinutesFromCalgary: 35,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Strathmore aerodrome",
      distanceToAerodromeKm: 9,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 9 km from Strathmore aerodrome, Dalemead lies outside the charted Calgary-area control zones, and no authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The country around Dalemead is close to level, and Dalemead Lake barely interrupts it — the water sits almost flush with the surrounding prairie, so the horizon runs unbroken in every direction from the hamlet.",
      consequence:
        "Flat land gives a ground-level camera nothing to work with. Every frame puts the horizon at mid-height, and a property's extent, its position against Highway 22X and its distance to the water all vanish into one thin band of foreground.",
      resolution:
        "Altitude replaces the missing relief. A top-down series or a boundary-line orbit turns level ground into a legible plan — the parcel, the road and the lake edge in one frame — delivered in the standard turnaround.",
    },
    landmarks: [
      "Dalemead Lake",
      "Dalemead Community Hall",
      "Highway 22X",
      "Dalemead Road",
    ],
    terrain:
      "Near-level prairie cropland broken only by Dalemead Lake, whose water sits nearly flush with the surrounding fields; the horizon runs unbroken past Highway 22X in every direction.",
    windNote:
      "Squarely in the chinook belt, with open ground on every side. West wind arrives at Dalemead with a long unbroken run, so calm conditions are a scheduling decision rather than an assumption.",
    seasonalWindow:
      "The lake sets the calendar. Between ice-off and freeze-up, open water gives the level prairie its one strong contrast; after freeze-up the frame simplifies to field pattern, and harvest lays the strongest texture across it.",
    bylawNote:
      "Municipal land in Rocky View County carries the county's own park and public-land rules, so launch and landing default to the subject property with the owner's consent.",
    subjectMix: ["agricultural", "municipal-infrastructure"],
    mobilisationNote:
      "About 35 minutes from the Calgary base with no authorisation lead time, so a Dalemead booking moves at the speed of the weather. Langdon and Indus sit close by, and grouping them on one trip east is the efficient way to work this corridor.",
    faqs: [
      {
        question: "Do you need NAV CANADA authorisation to fly a drone at Dalemead?",
        answer:
          "Normally no. Dalemead lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 9 km from Strathmore aerodrome, so no airspace authorisation is usually required. The specific site and altitude are still confirmed against NAV Drone before every flight, which is the standard final check.",
      },
      {
        question: "What does Dalemead's flat terrain mean for aerial photography?",
        answer:
          "It means altitude does the work that relief cannot. From the ground every view is a thin band under a large sky; from above, a parcel becomes a plan — its boundaries, its access from Highway 22X and its position against Dalemead Lake all read in a single frame.",
      },
      {
        question: "How quickly can a Dalemead shoot be scheduled?",
        answer:
          "Quickly, in most cases. Dalemead is about 35 minutes from the Calgary base in airspace with no authorisation lead time, so the schedule moves at the speed of the weather. It also pairs naturally with Langdon or Indus on a single trip east, which suits multi-property days.",
      },
    ],
    primaryKeywords: [
      "drone photography Dalemead",
      "aerial photography Dalemead",
      "Dalemead drone services",
    ],
    nearestLocations: ["langdon", "indus", "standard"],
  },

  // ──────────────────────────────────────────────────────────────── DALROY ──
  {
    slug: "dalroy",
    name: "Dalroy",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.1275, lng: -113.8792 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Dalroy sits about 9 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Dalroy's built form is the survey grid itself. From the air the hamlet is a loose gathering of yard sites along Dalroy Road, each one a treed island inside open cropland, with Range Road 285 and Township Road 253 ruling the pattern into squares.",
      consequence:
        "Ground photography shows the buildings and loses the holding. For a yard site or acreage here, the arrangement — house, outbuildings, shelterbelt and the land they command — is the asset, and none of it fits in an eye-level frame.",
      resolution:
        "A rising reveal over the yard puts the whole composition back together, from rooflines to fence lines. Because Dalroy sits in controlled airspace, the NAV CANADA authorisation is filed as part of booking, with delivery in the standard turnaround.",
    },
    landmarks: [
      "Dalroy Road",
      "Range Road 285",
      "Township Road 253",
      "Dalroy Community Grounds",
    ],
    terrain:
      "Open grain country ruled into squares by the range-road grid, with treed yard sites scattered as the only vertical relief; the land runs level toward the city with long sightlines in every direction.",
    windNote:
      "Chinook-belt farmland with open exposure. Shelterbelts are the only windbreaks in this landscape — which is why the yards planted them — and on gusty days they mark the calm on the ground without calming anything at flying altitude.",
    seasonalWindow:
      "Shelterbelts hold the frame together year-round while the fields around them cycle from seeded rows to stubble. Late light after green-up gives the yards their strongest separation, and fresh snow reduces the grid to graphic black and white.",
    bylawNote:
      "Rocky View County's public-land rules apply to road allowances and municipal parcels; the working default is takeoff and landing from the client's own property with permission.",
    subjectMix: ["agricultural", "industrial-yard"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. The CYYC authorisation is filed as part of booking, and Dalroy pairs naturally with Delacour, Balzac or Conrich on a single mobilisation.",
    faqs: [
      {
        question: "What airspace rules apply to a drone shoot in Dalroy?",
        answer:
          "Dalroy sits about 9 km from Calgary International, inside the CYYC control zone as charted, so flights are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. The exact site and altitude are verified against NAV Drone before every flight.",
      },
      {
        question: "Can a Dalroy shoot be combined with nearby communities?",
        answer:
          "Yes, and it usually should be. Dalroy is about 25 minutes from the Calgary base, with Delacour, Balzac and Conrich a short drive away, so one trip can cover a yard site here and a neighbouring property the same day. Grouping keeps the mobilisation efficient for everyone.",
      },
      {
        question: "What subjects photograph well from the air at Dalroy?",
        answer:
          "Working land, mostly. Yard sites, equipment lines and the field grid around Range Road 285 all read cleanly from altitude, and the industrial land toward Conrich photographs well for owners and brokers. Inside this airspace, each site's flight altitude is set by the NAV CANADA authorisation.",
      },
    ],
    primaryKeywords: [
      "drone photography Dalroy",
      "aerial photography Dalroy",
      "Dalroy drone services",
    ],
    nearestLocations: ["delacour", "balzac", "keoma", "conrich"],
  },

  // ────────────────────────────────────────────────────────────── DELACOUR ──
  {
    slug: "delacour",
    name: "Delacour",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.1543, lng: -113.7733 },
    tier: 4,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 16,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 16 km from Airdrie aerodrome, Delacour lies outside the charted Calgary-area control zones, and no authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Delacour is addressed by its roads. The golf course has a named road of its own — Delacour Golf Course Road — and everything else here sits at coordinates on the grid, where Delacour Road meets Range Road 282 and Township Road 251.",
      consequence:
        "For a rural property or venue, the buyer's first practical question is the route, and a ground-level photograph cannot answer it. The relationship between gate, road and destination is exactly the information that eye-level frames drop.",
      resolution:
        "An approach run flown along Delacour Road answers it directly — the road leads the eye to the property and the frame resolves over the site, showing the setting and the way in, delivered in the standard turnaround.",
    },
    landmarks: [
      "Delacour Golf Club",
      "Delacour Creek Valley",
      "Delacour Community Hall",
      "Delacour Road",
      "Range Road 282",
    ],
    terrain:
      "Gently rolling farmland folded once by the Delacour Creek Valley; the golf course's turf and tree plantings sit as a managed green block inside otherwise open cropland.",
    windNote:
      "Chinook-belt country. The land is open enough that west wind carries across it largely unchecked; the Delacour Creek Valley adds the one fold of shelter and texture in an otherwise even flow.",
    seasonalWindow:
      "Irrigated turf at the golf club greens ahead of the surrounding fields and holds after they cure, so the contrast between fairway and cropland is strongest outside high summer. Snow flattens that distinction and shifts the shot list to the creek valley and the road grid.",
    bylawNote:
      "The county's park and public-land rules govern launches on municipal ground at Delacour, so flights are staged from private property — the subject parcel or the venue — with consent.",
    subjectMix: ["commercial-retail", "agricultural"],
    mobilisationNote:
      "About 30 minutes from the Calgary base with no authorisation lead time. Delacour groups well with Dalroy, Balzac or Kathyrn on one trip, which suits multi-property days in this corridor.",
    faqs: [
      {
        question: "Can a drone be flown at Delacour without special approval?",
        answer:
          "In most cases, yes. Delacour lies in uncontrolled Class G airspace outside the charted Calgary-area control zones, about 16 km from Airdrie aerodrome, so no airspace authorisation is normally required. The specific site and altitude are still checked against NAV Drone before every flight goes ahead.",
      },
      {
        question: "Can you photograph the Delacour Golf Club from the air?",
        answer:
          "Yes, with the club's consent for launch and a flight plan built around play. Aerial frames are how a course shows its routing, its turf and its setting against the Delacour Creek Valley — relationships a ground camera cannot compress into a single image at any focal length.",
      },
      {
        question: "How does the drive time affect booking at Delacour?",
        answer:
          "Delacour is about 30 minutes from the Calgary base, close enough for same-day work when the weather cooperates. It also groups well with Dalroy, Balzac or Kathyrn on one trip, which is the efficient way to cover several rural properties across this corridor in a single booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Delacour",
      "aerial photography Delacour",
      "Delacour drone services",
    ],
    nearestLocations: ["dalroy", "balzac", "kathyrn", "conrich"],
  },

  // ─────────────────────────────────────────────────────────────── KATHYRN ──
  {
    slug: "kathyrn",
    name: "Kathyrn",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.2167, lng: -113.5167 },
    tier: 4,
    driveMinutesFromCalgary: 45,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Beiseker aerodrome",
      distanceToAerodromeKm: 19,
      authorizationRequired: false,
      authorizationNote:
        "Kathyrn lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 19 km from Beiseker aerodrome. No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Kathyrn keeps its grain elevator, and from the air it is the only vertical for miles — the hamlet's lots line up along Kathyrn Road facing it, the classic shape of a siding settlement with the elevator as its full stop.",
      consequence:
        "At ground level the elevator either dominates the frame or falls out of it. The relationship that gives Kathyrn its identity — a small grid of lots gathered around one tall working building — cannot be seen from a road shoulder at all.",
      resolution:
        "An orbit flown at the elevator's height holds the landmark and the hamlet in one moving frame, and a rising reveal sets any property here against the structure that names the place — delivered in the standard turnaround.",
    },
    landmarks: [
      "Kathyrn Grain Elevator",
      "Kathyrn Community Hall",
      "Highway 9",
      "Kathyrn Road",
    ],
    terrain:
      "Broad, open grain land at the corridor's eastern edge, flat enough that the elevator carries the skyline alone; field pattern and the road grid supply all the structure in an aerial frame.",
    windNote:
      "Open chinook-belt prairie. Wind that left the foothills hours earlier arrives here with nothing subtracted, and the elevator is tall enough to shed its own turbulence on the lee side, so orbits are planned with the wind in mind.",
    seasonalWindow:
      "Harvest is the signature window — cut lines and swaths radiate from the hamlet and the elevator reads against worked fields. Green-up runs a close second, and midwinter reduces the scene to weathered wood on white.",
    bylawNote:
      "Rocky View County's rules cover municipal land and road allowances here; launch and landing are planned from private property with the owner's permission as the default.",
    subjectMix: ["agricultural", "municipal-infrastructure"],
    mobilisationNote:
      "About 45 minutes from the Calgary base — the far edge of the corridor, so bookings here are grouped with Keoma or Delacour where possible and scheduled around the light rather than the drive.",
    faqs: [
      {
        question: "Is drone flight restricted around Kathyrn?",
        answer:
          "No. Kathyrn lies in uncontrolled Class G airspace outside the charted Calgary-area control zones, about 19 km from Beiseker aerodrome, so no authorisation is normally required. As with every flight, the specific site and altitude are confirmed against NAV Drone before the aircraft goes up.",
      },
      {
        question: "Can you photograph the Kathyrn Grain Elevator?",
        answer:
          "Yes, with the landowner's permission for launch. The elevator is the strongest aerial subject in the hamlet — an orbit flown at its height holds the structure, the lots gathered around it and the open grain country beyond in one continuous shot that no ground position can offer.",
      },
      {
        question: "Does the 45-minute drive change how a Kathyrn booking works?",
        answer:
          "Slightly. At about 45 minutes out, Kathyrn is the far edge of this corridor, so bookings are grouped where possible — a shoot here pairs naturally with Keoma or Delacour on the same trip. The schedule is planned around the light, because golden hour is the real constraint.",
      },
    ],
    primaryKeywords: [
      "drone photography Kathyrn",
      "aerial photography Kathyrn",
      "Kathyrn drone services",
    ],
    nearestLocations: ["keoma", "delacour", "balzac"],
  },

  // ───────────────────────────────────────────────────────────────── KEOMA ──
  {
    slug: "keoma",
    name: "Keoma",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.2, lng: -113.6167 },
    tier: 4,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Strathmore aerodrome",
      distanceToAerodromeKm: 20,
      authorizationRequired: false,
      authorizationNote:
        "Keoma lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 20 km from Strathmore aerodrome. No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Keoma's airspace record is short on purpose: Class G overhead, the nearest aerodrome about 20 km away at Strathmore, and no control zone charted over the hamlet. The regulatory geometry that shapes flights closer to the city runs out before it reaches Keoma Crossroads.",
      consequence:
        "Clients often assume every shoot near Calgary carries airport paperwork and lead time, so they book late and pad the schedule. Here that assumption costs them the good weather window, because the actual constraints at Keoma are wind and light, not authorisation.",
      resolution:
        "Booking is planned against the forecast instead of the paperwork — a golden-hour hold or a short-notice flight is realistic at Keoma, confirmed against NAV Drone as the final check and delivered in the standard turnaround.",
    },
    landmarks: [
      "Keoma Crossroads",
      "Keoma Community Grounds",
      "Highway 566",
      "Keoma Road",
      "Range Road 275",
    ],
    terrain:
      "Level to gently rolling grain country divided by the range-road grid; from altitude Keoma reads as a crossroads settlement with fields running unbroken to the horizon on every side.",
    windNote:
      "Chinook-belt prairie with open exposure in every direction. West wind crosses the corridor unimpeded, and afternoons typically run windier than mornings as daytime heating builds, so calm-light windows sit at either end of the day.",
    seasonalWindow:
      "The crop cycle is the palette. Seeded rows, green-up, swathing and stubble each give the grid around Keoma Crossroads a different texture, and low sun at either end of the day rakes that texture into relief. Snow turns the same grid graphic and simple.",
    bylawNote:
      "Municipal ground at Keoma falls under Rocky View County's park and public-land rules, so takeoff and landing are staged from the subject property with the owner's consent.",
    subjectMix: ["agricultural", "industrial-yard"],
    mobilisationNote:
      "About 40 minutes from the Calgary base. With no paperwork lead time, short-notice work is realistic, and Kathyrn, Dalroy and Delacour sit close enough to share the mobilisation.",
    faqs: [
      {
        question: "How does airspace affect booking a drone shoot in Keoma?",
        answer:
          "It barely does. Keoma lies in uncontrolled Class G airspace about 20 km from Strathmore aerodrome, outside the charted Calgary-area control zones, so no authorisation is normally required and no paperwork lead time applies. The site and altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "What is worth photographing from the air around Keoma?",
        answer:
          "Working agricultural land, first and foremost. Yard sites, field pattern and the crossroads settlement itself all read strongly from altitude, and Highway 566 gives equipment and yard operations a natural approach-run subject. The strongest material follows the crop cycle rather than any fixed calendar date.",
      },
      {
        question: "Can Keoma be shot on the same trip as neighbouring hamlets?",
        answer:
          "Yes. Keoma is about 40 minutes from the Calgary base, and Kathyrn, Dalroy and Delacour all sit within a short drive, so one mobilisation can cover several properties across the corridor. Grouping shoots this way is the most efficient booking for this part of Rocky View County.",
      },
    ],
    primaryKeywords: [
      "drone photography Keoma",
      "aerial photography Keoma",
      "Keoma drone services",
    ],
    nearestLocations: ["kathyrn", "dalroy", "delacour"],
  }
];
